import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Download, Eye, LogOut } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminSubmissions = () => {
  const [submissions, setSubmissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubmissions();
  }, []);

  const fetchSubmissions = async () => {
    try {
      const response = await axios.get(`${API}/admin/submissions`, {
        withCredentials: true
      });
      setSubmissions(response.data);
      
      // Check for new submissions and send notification
      const newSubmissions = response.data.filter(sub => sub.status === "new");
      if (newSubmissions.length > 0) {
        sendNewSubmissionNotification(newSubmissions[0]); // Notify for the newest one
      }
    } catch (error) {
      console.error("Fetch error:", error);
      if (error.response?.status === 401) {
        toast.error("Please login to access admin area");
        navigate("/admin/login");
      } else {
        toast.error("Failed to fetch submissions");
      }
    } finally {
      setLoading(false);
    }
  };

  const sendNewSubmissionNotification = (submission) => {
    // Check if we've already notified about this submission in this session
    const notifiedKey = `notified_${submission.id}`;
    if (sessionStorage.getItem(notifiedKey)) {
      return; // Already notified in this session
    }

    // Create notification email
    const reviewLink = `${window.location.origin}/admin/review/${submission.id}`;
    const emailBody = `New CoverDocket Submission Received!

Client Name: ${submission.client_name}
Client Email: ${submission.client_email}
Submitted: ${new Date(submission.created_at).toLocaleString()}
${submission.notes ? `Notes: ${submission.notes}` : ''}

Review this submission here:
${reviewLink}

---
CoverDocket Notification`;

    const subject = encodeURIComponent(`New Hearing Notice Submitted - ${submission.client_name}`);
    const body = encodeURIComponent(emailBody);
    const adminEmail = "therecruitingpartner@gmail.com";
    
    const gmailLink = `https://mail.google.com/mail/?view=cm&fs=1&to=${adminEmail}&su=${subject}&body=${body}`;
    
    // Open Gmail notification
    window.open(gmailLink, '_blank');
    
    // Mark as notified in this session
    sessionStorage.setItem(notifiedKey, 'true');
    
    toast.info("New submission notification opened in Gmail!");
  };

  const handleDownloadCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/ledger/csv`, {
        withCredentials: true,
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "ledger.csv");
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Ledger CSV downloaded successfully");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download ledger");
    }
  };

  const handleDownloadDOCX = async () => {
    try {
      const response = await axios.get(`${API}/admin/ledger/docx`, {
        withCredentials: true,
        responseType: "blob"
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "ledger.docx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success("Ledger Word document downloaded successfully");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download ledger");
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusClass = (status) => {
    const statusMap = {
      new: "status-new",
      reviewed: "status-assigned",
      assigned: "status-covered"
    };
    return `status-badge ${statusMap[status] || "status-new"}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F5F5] to-[#e8f4f8]">
      {/* Header */}
      <div className="header-gradient text-white py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-4">
            <img 
              src="/coverdocket-logo.png" 
              alt="CoverDocket Logo" 
              className="h-14 w-auto"
            />
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Admin Dashboard</h1>
              <p className="mt-1 text-lg opacity-90">Manage hearing submissions and assignments</p>
            </div>
          </div>
          <Button
            onClick={() => navigate("/admin/login")}
            variant="outline"
            className="bg-white/10 border-white/20 text-white hover:bg-white/20"
            data-testid="logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <Card className="glass-card border-0">
          <CardHeader>
            <div className="flex justify-between items-center">
              <div>
                <CardTitle className="text-2xl font-semibold text-[#0A1128]">
                  Submissions
                </CardTitle>
                <CardDescription className="text-base mt-1">
                  Review and process client hearing notices
                </CardDescription>
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => navigate("/admin/attorneys")}
                  variant="outline"
                  className="border-[#6497B1] text-[#6497B1] hover:bg-[#6497B1] hover:text-white"
                  data-testid="view-attorneys-button"
                >
                  View Attorney Directory
                </Button>
                <Button
                  onClick={handleDownloadCSV}
                  className="btn-primary"
                  data-testid="download-ledger-csv-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download CSV
                </Button>
                <Button
                  onClick={handleDownloadDOCX}
                  className="btn-secondary"
                  data-testid="download-ledger-docx-button"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Word
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-pulse text-[#6497B1] text-lg">Loading submissions...</div>
              </div>
            ) : submissions.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <p className="text-lg">No submissions yet.</p>
                <p className="text-sm mt-2">Client submissions will appear here.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-semibold">Submission ID</TableHead>
                      <TableHead className="font-semibold">Client Name</TableHead>
                      <TableHead className="font-semibold">Email</TableHead>
                      <TableHead className="font-semibold">Submitted</TableHead>
                      <TableHead className="font-semibold">Status</TableHead>
                      <TableHead className="font-semibold text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {submissions.map((sub) => (
                      <TableRow key={sub.id} className="table-row-hover" data-testid={`submission-row-${sub.id}`}>
                        <TableCell className="font-mono text-sm">{sub.id}</TableCell>
                        <TableCell className="font-medium">{sub.client_name}</TableCell>
                        <TableCell>{sub.client_email}</TableCell>
                        <TableCell className="text-sm text-gray-600">
                          {formatDate(sub.created_at)}
                        </TableCell>
                        <TableCell>
                          <span className={getStatusClass(sub.status)}>
                            {sub.status}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            onClick={() => navigate(`/admin/review/${sub.id}`)}
                            size="sm"
                            className="btn-primary"
                            data-testid={`review-button-${sub.id}`}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            Review
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSubmissions;